import AppShell from './AppShell';
import { UIProvider } from './state/UIContext';
import { WorklogProvider } from './state/WorklogContext';
import { StatusProvider } from './state/StatusContext';
import { AuthProvider, useAuth } from './state/AuthContext';
import { SignInPage } from './components/SignInPage';
import TdCatcher from './components/Pages/TdCatcher';
import { PushPage } from './components/Pages/PushPage';
import RefPage from './components/Pages/RefPage';
import { OwnerNav } from './components/OwnerNav';

// The standalone owner pages (/td, /push, /ref) get the same owner links at the
// top, so navigation between surfaces is available from every page, not only the
// dashboard. A small padded wrapper keeps the right-aligned links off the edge.
function OwnerPage({ path, children }: { path: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '12px 16px 0' }}>
      <OwnerNav current={path} />
      {children}
    </div>
  );
}

// Session gate. Every route below sits behind it: no session, no app. While
// /api/auth/me is in flight we render nothing rather than flashing either the
// sign-in form or the dashboard, because guessing wrong in either direction is
// how this app ended up showing month-old numbers as if they were current.
//
// ownerOnly routes (/push, /ref) additionally require role==='owner', so Jenny
// signs in to the same site and simply does not have them.
function Gate({ children, ownerOnly = false }: { children: React.ReactNode; ownerOnly?: boolean }) {
  const { role, loading } = useAuth();
  if (loading) return null;
  if (role === 'anonymous') return <SignInPage />;
  if (ownerOnly && role !== 'owner') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#8fb3d4', font: '14px ui-monospace, monospace',
      }}>Not available for this account.</div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  // Lightweight pathname-based routing. /td and /push render dedicated pages
  // WITHOUT the sonographer WorklogProvider (which would fire unneeded fetches).
  // Any other path falls through to the existing sonographer dashboard flow.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isTd = path.startsWith('/td');
  const isPush = path.startsWith('/push');
  const isRef = path.startsWith('/ref');

  if (isRef) {
    return (
      <AuthProvider>
        <UIProvider>
          <Gate ownerOnly><OwnerPage path="/ref"><RefPage /></OwnerPage></Gate>
        </UIProvider>
      </AuthProvider>
    );
  }

  if (isTd) {
    return (
      <AuthProvider>
        <UIProvider>
          <Gate ownerOnly><OwnerPage path="/td"><TdCatcher /></OwnerPage></Gate>
        </UIProvider>
      </AuthProvider>
    );
  }

  if (isPush) {
    return (
      <AuthProvider>
        <UIProvider>
          <Gate ownerOnly><OwnerPage path="/push"><PushPage /></OwnerPage></Gate>
        </UIProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <UIProvider>
        <Gate>
          <StatusProvider>
            <WorklogProvider>
              <AppShell />
            </WorklogProvider>
          </StatusProvider>
        </Gate>
      </UIProvider>
    </AuthProvider>
  );
}
